import { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { ButtonPrimary } from '../../../../../../../baseComponents/Button'
import InputTableLeftColumnForm from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { OrderHeaderURLs, SetCompleteDataTy } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import ModalDeleteSellOrderAccount from './ModalDeleteQuoteOrderAccount'
import { QuoteOrderAccountModelType } from '@teinor/erp/types/company/order/quote/quoteOrder/quoteOrderAccount'
import { flushSync } from 'react-dom'
import '../quoteHeader.sass'

type QuoteStepHeaderAccountsProps = InputTableLeftTabsProps & QuoteCreateStepHeaderAccountLogicProps & {
    headerData: TwinDictionary
    getCompleteData: () => void
}

const QuoteStepHeaderAccounts: React.FC<QuoteStepHeaderAccountsProps> = ({ headerData, setCompleteData, orderHeaderURLs, getCompleteData }) => {
    const { t } = useTwinTranslation()
    const accounts = headerData.OrderAccounts
    const { myHandleAdd, myHandleChange, openModal, setOpenModal } = useQuoteCreateStepHeaderAccountLogic({ setCompleteData, orderHeaderURLs, accounts })
    const accountsSections: FormRender<any> = []
    if (accounts?.length) {
        for (let key in accounts) {
            const account = accounts[key]
            if (account.name !== '--other') {
                accountsSections.push({
                    cols: 1,
                    className: `quoteOrderAccount ${parseInt(key) === (accounts.length - 1) ? 'isLast' : ''}`,
                    elements: [
                        {
                            name: key + '-name',
                            component: 'InputWithLabelMarginRemove',
                            label: t('name', 'Nombre'),
                            defaultValue: account?.name,
                            removeFunct: () => setOpenModal({
                                type: 'delete',
                                allRowData: { 'name': account.name, 'id': account.id, 'OrderId': headerData.id }
                            })
                        },
                        {
                            name: key + '-description',
                            component: 'TextArea',
                            label: t('description', 'Description'),
                            value: account?.description,
                        },
                    ]
                })
            }
        }
    }
    const sections: FormRender<any> = [
        ...accountsSections,
        {
            cols: 1,
            elements: [
                {
                    name: 'add',
                    component: 'Custom',
                    render: () => <ButtonPrimary onClick={() => myHandleAdd()} type='button'>{t('add', 'Añadir')}</ButtonPrimary>,
                },
            ]
        }
    ]
    return (
        <div className='flex flex-col flex-auto'>
            <InputTableLeftColumnForm form={sections} headerData={headerData} handleChange={myHandleChange} />
            {openModal?.type === 'delete' ? <ModalDeleteSellOrderAccount getCompleteData={getCompleteData} orderHeaderURLs={orderHeaderURLs} setOpened={() => setOpenModal(null)} onSubmit={() => { }} allRowData={openModal.allRowData}/> : null}
        </div>
    )
}

interface QuoteCreateStepHeaderAccountLogicProps {
    setCompleteData: SetCompleteDataTy
    orderHeaderURLs: OrderHeaderURLs
    accounts: QuoteOrderAccountModelType[]
}

const useQuoteCreateStepHeaderAccountLogic = ({ setCompleteData, orderHeaderURLs, accounts }: QuoteCreateStepHeaderAccountLogicProps) => {
    const { t } = useTwinTranslation()
    const params = useParams<{ id: string }>()
    const id = params.id
    const chapterText = t('chapter', 'Capítulo')
    const { openModal, setOpenModal } = useEditDeleteModal()

    const myHandleAdd = useCallback(async () => {
        const accountLength = accounts.length
        const order = parseInt(String(accounts[accountLength -1]?.order || '0')) + 1
        const account = await twinFetchPostJSON(orderHeaderURLs.createAccount, {
            name: chapterText + ' ' + order,
            OrderId: id
        })
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData?.header?.OrderAccounts?.push(account)
            return { ...myData }
        })
    }, [id, setCompleteData, orderHeaderURLs, chapterText, accounts])

    const myHandleChange = useCallback(async (id: string, field: string, value: any) => {
        await flushSync(async () => {
            let account: TwinDictionary = {}
            await setCompleteData((old) => {
                if (!old) {
                    return null
                }
                const [key, myField] = field.split('-')
                const myData = JSON.parse(JSON.stringify(old))
                myData.header.OrderAccounts[key][myField] = value
                account = myData.header.OrderAccounts[key]
                return { ...myData }
            })
            if (account) {
                await twinFetchPostJSON(orderHeaderURLs.updateAccount, { ...account })
            }
        })
    }, [setCompleteData, orderHeaderURLs])

    return { myHandleAdd, myHandleChange, openModal, setOpenModal }
}

export default QuoteStepHeaderAccounts